<script>
import moment from "moment";
import axios from '@/utils/axios';
import { clientAdmin } from "@/utils/auth";

export default {
  inject: ['setLoading'],
  data() {
    return {
      errors: {},
      title: "HRM",
      text: "Asset",
      text1: "Assignments",
      clientAdmin: clientAdmin(),
      filter: {
        asset_id: '',
        employee_id: '',
        status: '',
        page: 1,
      },
      statuses: [
        { id: 'active', name: 'Active' },
        { id: 'returned', name: 'Returned' },
      ],
      data: [],
      pagination: {},
      noDataMessage: '',
      assets: [],
      employees: [],
      selectedAssignment: null,
    };
  },

  mounted() {
    this.getDropdowns();
    this.filterReport();
  },

  methods: {

    async filterReport(resetPage = true) {
      this.errors = {};
      if (resetPage) this.filter.page = 1;
      this.setLoading(true);
      try {
        const query_string = new URLSearchParams(this.filter).toString();
        const response = await axios.get(`/hrms/assets/assignments?${query_string}`);

        if (!response.data.status) {
          this.data = [];
          this.pagination = {};
          this.noDataMessage = response.data.message || 'No records found.';
          return;
        }

        this.noDataMessage = '';
        const rows = response.data.data || {};
        this.data = rows.data || [];
        this.pagination = rows;
      } catch (error) {
        this.data = [];
        this.pagination = {};
        this.noDataMessage = 'No records found.';
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    goToPage(page) {
      if (!page || page === this.pagination.current_page) return;
      this.filter.page = page;
      this.filterReport(false);
    },

    async getDropdowns() {
      try {
        const [assetRes, empRes] = await Promise.allSettled([
          axios.get('/hrms/assets', { params: { per_page: 1000 } }),
          axios.get('/hrms/employees', { params: { per_page: 1000 } }),
        ]);
        this.assets = assetRes.status === 'fulfilled' ? (assetRes.value.data.data.data || []) : [];
        this.employees = empRes.status === 'fulfilled' ? (empRes.value.data.data.data || []) : [];
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    hasActiveFilter() {
      return this.filter.asset_id || this.filter.employee_id || this.filter.status;
    },

    clearFilter() {
      this.filter.asset_id = '';
      this.filter.employee_id = '';
      this.filter.status = '';
      this.filterReport();
    },

    employeeName(row) {
      const name = row.employee?.full_name;
      return (name && name.trim()) || row.employee?.code || '-';
    },

    formatDate(val) {
      if (!val) return '-';
      return moment(val).format('DD MMM YYYY');
    },

    statusClass(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'active') return 'bg-success';
      if (s === 'returned') return 'bg-secondary';
      return 'bg-light text-dark';
    },

    conditionClass(condition) {
      const c = String(condition || '').toLowerCase();
      if (c === 'excellent' || c === 'good') return 'text-success';
      if (c === 'fair') return 'text-warning';
      if (c === 'poor' || c === 'damaged') return 'text-danger';
      return 'text-muted';
    },

    openReturnModal(row) {
      this.selectedAssignment = row;
      this.$nextTick(() => this.$openModal('hrm_asset_return_modal'));
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <router-link to="/hrms/assets" class="btn btn-outline-secondary d-flex align-items-center">
            <i class="ti ti-arrow-back-up me-2"></i>Back to Assets
          </router-link>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.asset_id" :options="assets" label="name" :reduce="a => a.id"
                placeholder="Asset" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.employee_id" :options="employees" label="full_name" :reduce="e => e.id"
                placeholder="Employee" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.status" :options="statuses" label="name" :reduce="s => s.id"
                placeholder="Status" :clearable="true" @update:modelValue="filterReport()" style="min-width: 160px;"></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                    <tr>
                    <th>#</th>
                    <th>Asset</th>
                    <th>Employee</th>
                    <th>Qty</th>
                    <th>Assigned Date</th>
                    <th>Condition (Out)</th>
                    <th>Returned Date</th>
                    <th>Condition (In)</th>
                    <th>Assigned By</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id || index">
                        <td>{{ (pagination.per_page * (pagination.current_page - 1)) + index + 1 }}.</td>
                        <td>
                            <strong class="text-dark">{{ row.asset?.name || '-' }}</strong>
                            <small class="text-muted d-block" style="font-size:11px;">{{ row.asset?.code || '' }}</small>
                        </td>
                        <td>
                            <strong class="text-dark">{{ employeeName(row) }}</strong>
                            <small class="text-muted d-block" style="font-size:11px;">{{ row.employee?.code || '' }}</small>
                        </td>
                        <td>{{ row.quantity }}</td>
                        <td>{{ formatDate(row.assigned_date) }}</td>
                        <td>
                          <span class="text-capitalize" :class="conditionClass(row.condition_at_assignment)">{{ row.condition_at_assignment || '-' }}</span>
                        </td>
                        <td>{{ formatDate(row.returned_date) }}</td>
                        <td>
                          <span class="text-capitalize" :class="conditionClass(row.condition_at_return)">{{ row.condition_at_return || '-' }}</span>
                        </td>
                        <td>{{ row.assigned_by?.name || '-' }}</td>
                        <td>
                            <span class="badge rounded-pill" :class="statusClass(row.status)">
                            {{ row.status }}
                            </span>
                        </td>
                        <td class="text-end">
                          <button class="btn btn-sm btn-outline-primary" v-if="row.status === 'active'" @click="openReturnModal(row)">
                            <i class="ti ti-arrow-back-up me-1"></i>Return
                          </button>
                        </td>
                        </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">{{ noDataMessage || 'No records found.' }}</div>

              <!-- Pagination -->
              <div class="d-flex align-items-center justify-content-between p-3" v-if="pagination.last_page > 1">
                <span class="text-muted" style="font-size:12px;">
                  Showing {{ pagination.from || 0 }} to {{ pagination.to || 0 }} of {{ pagination.total || 0 }}
                </span>
                <nav>
                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(pagination.current_page - 1)">Previous</a>
                    </li>
                    <li class="page-item" v-for="p in pagination.last_page" :key="p" :class="{ active: p === pagination.current_page }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(p)">{{ p }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(pagination.current_page + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-asset-return-modal :assignment="selectedAssignment" @returned="filterReport(false)" />
</template>